import {
	Box,
	Button,
	CircularProgress,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormHelperText,
	Grid,
	IconButton,
	InputLabel,
	OutlinedInput,
	Stack,
	TextField,
} from '@mui/material';
import { FC, useEffect } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { z } from 'zod';
import { FormField } from '../../form/FormField';
import { Add, DeleteOutline } from '@mui/icons-material';
import { graphql } from '../../gql';
import { useApiMutation, useApiQuery } from '../../query/hooks';
import { useFieldArray } from 'react-hook-form';
import { useModalContext } from '../../modal/ModalContext';
import { PersonaBlockUpdateItemsEntityRelationInput } from '../../gql/graphql';
import { useTypedForm } from '../../../../shared/fe/hooks/useForm';
import { mutationResponseHandler } from '../../utils/mutationResponseHandler';
import { useSnackbar } from '../../store/snackbar';

const personaBlockUpdateModal_personaBlock = graphql(`
	query personaBlockUpdateModal_personaBlock($blockId: UUID!) {
		getPersonaBlock(by: { id: $blockId }) {
			id
			name
			description
			color
			items {
				id
				name
				value
			}
		}
	}
`);

const personaDetail_updatePersonaBlock = graphql(`
	mutation personaDetail_updatePersonaBlock(
		$name: String!
		$description: String
		$color: String!
		$items: [PersonaBlockUpdateItemsEntityRelationInput!]
		$personaId: UUID!
		$blockId: UUID!
	) {
		updatePersonaBlock(
			by: { id: $blockId }
			data: { name: $name, description: $description, color: $color, items: $items, persona: { connect: { id: $personaId } } }
		) {
			ok
			errorMessage
		}
	}
`);

const updatePersonaBlockModalSchema = z.object({
	personaId: z.string(),
	blockId: z.string(),
});

const updateFormSchema = (intl: IntlShape) =>
	z.object({
		name: z.string().nonempty(intl.formatMessage({ id: 'form.persona.title.detail.block.error.empty-name' })),
		description: z.string().nullable(),
		items: z
			.array(
				z.object({
					id: z.string().nullable().optional(),
					name: z.string(),
				}),
			)
			.nullable(),
	});

type Props = z.infer<typeof updatePersonaBlockModalSchema>;

type FormValues = z.TypeOf<ReturnType<typeof updateFormSchema>>;

const PersonaBlockUpdate: FC<Props> = ({ personaId, blockId }) => {
	const intl = useIntl();
	const { closeModal } = useModalContext();
	const { openSnackbar } = useSnackbar();

	const { data: personaBlockData, isLoading } = useApiQuery(personaBlockUpdateModal_personaBlock, { variables: { blockId } });

	const form = useTypedForm({
		schema: updateFormSchema(intl),
	});

	const { formState, control, reset, register, handleSubmit } = form;

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'items',
	});

	useEffect(() => {
		if (personaBlockData?.getPersonaBlock) {
			reset({
				name: personaBlockData.getPersonaBlock.name,
				description: personaBlockData.getPersonaBlock.description,
				items: personaBlockData.getPersonaBlock.items.length > 0 ? personaBlockData.getPersonaBlock.items : [],
			});
		}
	}, [personaBlockData?.getPersonaBlock, reset]);

	const { mutate: updateBlock } = useApiMutation(personaDetail_updatePersonaBlock, {
		onSuccess: async (result) => {
			mutationResponseHandler({
				result: result.updatePersonaBlock.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'persona.detail.block.edit.success' }),
					error: intl.formatMessage({ id: 'common.error.default' }),
				},
				errorMessage: result.updatePersonaBlock.errorMessage,
				openSnackbar,
			});
		},
	});

	const onSubmit = (data: FormValues) => {
		const items: PersonaBlockUpdateItemsEntityRelationInput[] =
			data.items?.map((item) => {
				return item.id ? { update: { by: { id: item.id ?? null }, data: { name: item.name } } } : { create: { name: item.name } };
			}) ?? [];
		const itemsToDelete =
			personaBlockData?.getPersonaBlock?.items.filter((item) => !data.items?.some((formItem) => formItem.id === item.id)) ?? [];
		itemsToDelete.forEach((item) => {
			items.push({ delete: { id: item.id } });
		});
		updateBlock(
			{
				name: data.name,
				description: data.description,
				color: personaBlockData?.getPersonaBlock?.color || 'red',
				personaId,
				blockId: blockId,
				items,
			},
			{ onSuccess: closeModal },
		);
	};

	if (isLoading) {
		return (
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
				<CircularProgress />
			</Box>
		);
	}

	if (personaBlockData?.getPersonaBlock == null) {
		return null;
	}

	return (
		<form noValidate onSubmit={handleSubmit(onSubmit)}>
			<DialogTitle>
				<FormattedMessage id={'form.persona.detail.block.title.update'} />
			</DialogTitle>
			<Divider />
			<DialogContent>
				<Grid container spacing={3}>
					<Grid item xs={12}>
						<Stack gap={3}>
							<FormField
								form={form}
								name={'name'}
								label={intl.formatMessage({ id: 'form.persona.title.detail.block.name' })}
								render={(props) => <OutlinedInput {...props} />}
							/>
							<FormField
								form={form}
								name={'description'}
								label={intl.formatMessage({ id: 'form.persona.title.detail.block.description' })}
								render={(props) => <TextField {...props} rows={5} multiline />}
							/>
							<Stack gap={1}>
								<InputLabel sx={{ fontWeight: 500 }}>
									<FormattedMessage id={'form.persona.title.detail.block.property-label'} />
								</InputLabel>
								<Stack gap={1}>
									{fields.map((item, index) => (
										<TextField
											{...register(`items.${index}.name`)}
											name={`items.${index}.name`}
											key={index}
											InputProps={{
												endAdornment: (
													<IconButton onClick={() => remove(index)}>
														<DeleteOutline />
													</IconButton>
												),
											}}
										/>
									))}
								</Stack>
							</Stack>
							<Stack direction={'row'} display={'flex'}>
								<Button variant={'text'} onClick={() => append({ name: '' })} startIcon={<Add sx={{ width: '18px', height: '18px' }} />}>
									<FormattedMessage id={'form.persona.title.detail.block.property-add'} />
								</Button>
							</Stack>
						</Stack>
					</Grid>

					{formState.errors.root != null && (
						<Grid item xs={12}>
							<FormHelperText error>{formState.errors.root.message}</FormHelperText>
						</Grid>
					)}
				</Grid>
			</DialogContent>
			<Divider />
			<DialogActions sx={{ p: 2.5 }}>
				<Stack direction={'row'} spacing={2} alignItems={'center'} width={'100%'} justifyContent={'end'}>
					<Button color={'secondary'} onClick={closeModal}>
						<FormattedMessage id={'common.cancel'} />
					</Button>
					<Button type={'submit'} variant={'contained'} disabled={formState.isSubmitting}>
						<FormattedMessage id={'common.save'} />
					</Button>
				</Stack>
			</DialogActions>
		</form>
	);
};

export { PersonaBlockUpdate, updatePersonaBlockModalSchema };
